import { useTranslation } from 'react-i18next'
import { Button, softBadgeColors, StarRating } from '@structural-climbing/ui'
import { disciplineForSection, gradeDisplay } from '@structural-climbing/validation'
import type { ClimbingRoute } from './types'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { CommentIcon } from './CommentIcon'

interface RouteRowProps {
  route: ClimbingRoute
  apiUrl: string
  onToggleStatus: (route: ClimbingRoute) => Promise<void>
  onViewDetail: (route: ClimbingRoute) => void
  onViewComments: (route: ClimbingRoute) => void
  isPending: boolean
}

export function RouteRow({
  route,
  apiUrl,
  onToggleStatus,
  onViewDetail,
  onViewComments,
  isPending,
}: RouteRowProps) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const grade = gradeDisplay(
    gradeConfig,
    route.grade,
    disciplineForSection(route.section?.discipline),
  )
  const badge = softBadgeColors(grade.color)

  return (
    <div
      className="flex cursor-pointer flex-col gap-2 px-4 py-3 transition-colors hover:bg-content2/60 sm:flex-row sm:items-center sm:gap-3"
      onClick={() => onViewDetail(route)}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className="h-4 w-4 flex-none rounded-full border border-black/10 shadow"
          style={{ backgroundColor: route.color ?? '#d4d4d8' }}
        />

        <span
          className="rounded px-2 py-0.5 text-xs font-bold"
          style={{ backgroundColor: badge.bg, color: badge.fg }}
        >
          {grade.label}
        </span>

        <div className="h-10 w-10 flex-none overflow-hidden rounded bg-default-100">
          {route.image_r2_key ? (
            <img
              src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
              alt={`${t('routes.grade')} ${grade.label}`}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-foreground/20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="truncate text-sm text-foreground/60">{route.setter?.name ?? '—'}</p>
          {route.section && (
            <span className="w-fit rounded-full bg-default-100 px-2 py-0.5 text-xs text-foreground/50">
              {route.section.name}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-none items-center justify-end gap-3 sm:justify-normal">
        <span className="flex-none text-xs text-foreground/40">
          {t('routes.ascentCount', { count: route.ascent_count })}
        </span>

        {route.avg_rating != null && (
          <StarRating
            value={route.avg_rating}
            showValue
            size="sm"
            className="flex-none text-xs text-foreground/40"
            label={t('routes.ratingOutOf', { value: route.avg_rating.toFixed(1) })}
          />
        )}

        <div className="flex gap-1">
          {/* Always shown, even at zero, so setters learn where comments land. */}
          <Button
            size="sm"
            variant="light"
            className={`min-w-0 gap-1 px-2 ${route.comment_count > 0 ? 'text-primary' : 'text-foreground/30'}`}
            aria-label={t('routes.commentsCount', { count: route.comment_count })}
            title={t('routes.commentsCount', { count: route.comment_count })}
            onClick={(e) => {
              e.stopPropagation()
              onViewComments(route)
            }}
          >
            <CommentIcon />
            {route.comment_count > 0 && (
              <span className="text-xs font-semibold tabular-nums">{route.comment_count}</span>
            )}
          </Button>
          <Button
            size="sm"
            variant="light"
            isLoading={isPending}
            isDisabled={isPending}
            onClick={(e) => {
              e.stopPropagation()
              void onToggleStatus(route)
            }}
          >
            {route.status === 'active' ? t('routes.archive') : t('routes.restore')}
          </Button>
        </div>
      </div>
    </div>
  )
}
